import type { IBreakPoint, IPriceCalculatorResult } from "../types/price-calculator.types";

export default class PriceCalculatorService {
  private quantity: number;

  constructor(quantity: number) {
    this.quantity = quantity;
  }

  public calculate(options?: { breakPoint?: IBreakPoint }): IPriceCalculatorResult | null {
    if (options?.breakPoint) {
      return this.calculateWithBreakPoint(options.breakPoint);
    }
    return null;
  }

  /**
   * Finds the most suitable tariff from the list of breakpoints.
   * Suitable is one where:
   * - min <= quantity
   * - and the largest of such min
   */
  public getMinBreakPoint(breakPointsList: IBreakPoint[]): IBreakPoint | null {
    if (!breakPointsList.length) {
      console.warn("Break points list is empty.");
      return null;
    }

    const eligibleRules = breakPointsList.filter((rule): rule is IBreakPoint & { min: number } => {
      return rule.min !== undefined && rule.min <= this.quantity;
    });

    if (eligibleRules.length > 0) {
      return eligibleRules.reduce((prev, curr) =>
        curr.min > prev.min ? curr : prev
      );
    }

    const sortedByMin = [...breakPointsList].sort((a, b) => {
      const aMin = a.min ?? Number.MIN_SAFE_INTEGER;
      const bMin = b.min ?? Number.MIN_SAFE_INTEGER;
      return aMin - bMin;
    });

    return sortedByMin[0] || null;
  }

  /**
   * Returns the calculated price based on the passed breakpoint.
   */
  private calculateWithBreakPoint(breakPoint: IBreakPoint): IPriceCalculatorResult {
    const pricePerEach = breakPoint.price;
    const totalPrice = pricePerEach * this.quantity;
    return { pricePerEach, totalPrice };
  }
}